/**
 * The app half of the web dictation bubble's injected UI host.
 *
 * The bubble lives in `@heidi/dictate` and renders app code it cannot import:
 * the CSAT thumbs button and form fields, the mic-selector bars, two app hooks,
 * the router and the native widget hide. Every member is a call-through, so no
 * imported binding is read while this module evaluates.
 *
 * Installed by a side-effect import from `src/components/layout/scribe-layout.tsx`,
 * the layout that mounts `DictationBubble`.
 */
import { Bars } from '@/components/mic-selector/Bars';
import { BarsBase } from '@/components/mic-selector/BarsBase';
import { DesktopBars } from '@/components/mic-selector/DesktopBars';
import { tauriWidgetPort } from '@/composition/hosts/dictation/dictation-widget/tauri-widget-port';
import { useScrollGradient } from '@/hooks/scroll/useScrollGradient';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import {
  configureDictationBubbleUiHost,
  type DictationBubbleUiHost,
} from '@heidi/dictate';
import { CsatPopoverFormFields, ThumbsTooltipButton } from '@heidi/growth';
import { useRouter } from 'next/router';

/** Binds today's app implementations to the dictation bubble's host. */
export const dictationBubbleUiHost: DictationBubbleUiHost = {
  ThumbsTooltipButton: (props) => <ThumbsTooltipButton {...props} />,
  CsatPopoverFormFields: (props) => <CsatPopoverFormFields {...props} />,
  Bars: (props) => <Bars {...props} />,
  DesktopBars: (props) => <DesktopBars {...props} />,
  BarsBase: (props) => <BarsBase {...props} />,
  useCopyToClipboard: () => useCopyToClipboard(),
  useScrollGradient: (ref, deps) => useScrollGradient(ref, deps),
  useAppRouter: () => useRouter(),
  hideDictationWidget: () => tauriWidgetPort.hide(),
};

configureDictationBubbleUiHost(dictationBubbleUiHost);
